import { useState, ChangeEvent } from 'react';
import Spinner from 'design-system/CircularProgress';
import { Button, TextInput } from 'react-scale/components/design-system';
import { makeStyles } from 'design-system/styles';
import _ from 'lodash';
import { SubtaskLintResult } from '@scale/shared/types/api/linting';
import {
  LintOutcome,
  LintVerdict,
  LintLevel,
  ScriptLinterMetadata,
  NormalLintResult,
  ResponseLinterRule,
  ScriptLinterName,
} from '@scale/shared/types/linter';
import Link from 'react-scale/corp/components/UI/IsomorphicLink/IsomorphicLink';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
  LintMetric,
  LintDismissalType,
  LintErrorWithDismissal,
} from '@scale/shared/task_lint/task_linter';

type ResponseLinterResult = NormalLintResult<ScriptLinterMetadata> & { metric?: LintMetric };

enum ResultLevel {
  Error = 'Error',
  Warning = 'Warning',
  Pass = 'Pass',
}

const lintLevelResultLevel: Record<LintLevel, ResultLevel> = {
  // error
  [LintLevel.BanTasker]: ResultLevel.Error,
  [LintLevel.Discard]: ResultLevel.Error,
  [LintLevel.Escalate]: ResultLevel.Error,
  [LintLevel.Redo]: ResultLevel.Error,
  [LintLevel.ExcludeFromConsensus]: ResultLevel.Error,
  [LintLevel.SendBackToAttempt]: ResultLevel.Error,
  [LintLevel.RuntimeError]: ResultLevel.Error,

  // warning
  [LintLevel.Warn]: ResultLevel.Warning,
  [LintLevel.Info]: ResultLevel.Warning,
};

const verdictLevel: Record<LintVerdict, ResultLevel> = {
  // Error
  [LintVerdict.Discard]: ResultLevel.Error,
  [LintVerdict.Escalate]: ResultLevel.Error,
  [LintVerdict.Redo]: ResultLevel.Error,
  [LintVerdict.SendBackToAttempt]: ResultLevel.Error,
  [LintVerdict.Error]: ResultLevel.Error,
  [LintVerdict.ExcludeFromConsensus]: ResultLevel.Error,

  // Warning
  [LintVerdict.NoAction]: ResultLevel.Warning,
  [LintVerdict.AsyncLint]: ResultLevel.Warning,

  // Pass
  [LintVerdict.Pass]: ResultLevel.Pass,
  [LintVerdict.NoLint]: ResultLevel.Pass,
};

const levelColor: Record<ResultLevel, string> = {
  [ResultLevel.Error]: '#f34236',
  [ResultLevel.Warning]: '#ff9800',
  [ResultLevel.Pass]: '#4caf50',
};

const levelIcon: Record<ResultLevel, FontAwesomeIconProps> = {
  [ResultLevel.Error]: { icon: 'times-square', color: levelColor.Error },
  [ResultLevel.Warning]: { icon: 'exclamation-triangle', color: levelColor.Warning },
  [ResultLevel.Pass]: { icon: 'check-square', color: levelColor.Pass },
};

const verdictStringRepresentation: Record<LintVerdict, string> = {
  [LintVerdict.Discard]: 'Discard',
  [LintVerdict.Escalate]: 'Escalate',
  [LintVerdict.Redo]: 'Redo',
  [LintVerdict.SendBackToAttempt]: 'Send Back To Attempt',
  [LintVerdict.Error]: 'Runtime Error',
  [LintVerdict.ExcludeFromConsensus]: 'Exclude From Consensus',

  [LintVerdict.NoAction]: 'No Action',
  [LintVerdict.AsyncLint]: 'Async Lint',

  [LintVerdict.Pass]: 'Pass',
  [LintVerdict.NoLint]: 'Pass (no lint)',
};

const verdictDescription: Record<LintVerdict, string> = {
  [LintVerdict.Discard]: 'the attempt was discarded',
  [LintVerdict.Escalate]: 'the subtask was sent to L11',
  [LintVerdict.Redo]: 'the subtask remained on the same review level where it was attempted',
  [LintVerdict.SendBackToAttempt]:
    'the subtask was sent back to attempt level, with the previous response',
  [LintVerdict.Error]: 'an unexpected error occurred while linting',
  [LintVerdict.ExcludeFromConsensus]: 'the attempt was excluded from consensus',

  [LintVerdict.NoAction]: 'info linters failed',
  [LintVerdict.AsyncLint]: 'still running (due to an async rule)',

  [LintVerdict.Pass]: 'none of the linters have found an issue',
  [LintVerdict.NoLint]: 'there were no lint rules to run on this task',
};

const EXTRA_CONTENT_ALLOWED_LINTERS = [
  ScriptLinterName.SensorFusionTextAnnotationLinter,
  ScriptLinterName.LidarCuboidShift,
];

type VerdictRepresentation = {
  color: string;
  stringRepr: string;
  description: string;
};

const parseDate = (dateString: string) => {
  const dateObj = new Date(dateString);

  const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getUTCDate()).padStart(2, '0');
  const year = String(dateObj.getUTCFullYear()).slice(-2);

  return `${month}/${day}/${year}`;
};

const Bold = ({ text }: { text: string }) => {
  return <span style={{ fontWeight: 'bold' }}>{text}</span>;
};

const ResponseLintResults = ({
  onSearch,
  isLoading,
  subtaskLintResults,
}: {
  onSearch: (subtaskId: string) => void;
  isLoading: boolean;
  subtaskLintResults?: SubtaskLintResult;
}) => {
  const classes = useStyles();
  const [searchSubtask, setSearchSubtask] = useState<string>('');

  const showLintResults = () => {
    if (isLoading) return <Spinner />;
    if (subtaskLintResults) return <LintResult subtaskLintResults={subtaskLintResults} />;
    return <></>;
  };

  return (
    <div style={{ maxWidth: '95%' }}>
      <h2>Response Lint Results</h2>

      <div className={classes.searchContainer}>
        <TextInput
          size="medium"
          value={searchSubtask}
          placeholder="Search subtask"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchSubtask(e.target.value);
          }}
          style={{ marginRight: 5 }}
        />
        <Button onClick={() => onSearch(searchSubtask)} style={{ marginLeft: 5 }} type="primary">
          Search
        </Button>
      </div>

      <svg height={40} width={'100%'} style={{ marginTop: '30px' }}>
        <line x1="100%" className={classes.line} />
      </svg>
      {showLintResults()}
    </div>
  );
};

const LintResult = ({ subtaskLintResults }: { subtaskLintResults: SubtaskLintResult }) => {
  const { subtaskId, attemptId, attemptedAt, attemptedBy, lintResults, lintVerdict } =
    subtaskLintResults;

  const verdictRepresentation: VerdictRepresentation = {
    color: levelColor[verdictLevel[lintVerdict]],
    stringRepr: verdictStringRepresentation[lintVerdict],
    description: verdictDescription[lintVerdict],
  };

  const showLintResults = () => {
    if (_.isEmpty(lintResults)) {
      return lintVerdict === LintVerdict.Pass ? 'Passed' : 'Could not find linting results';
    }
    return <LintResultsCards subtaskLintResults={subtaskLintResults} />;
  };

  return (
    <div style={{ width: '85%', marginLeft: '40px' }}>
      <div>
        <div>
          <h2>
            Subtask{' '}
            <Link href={`/lookup/${subtaskId}`} target="_blank">
              {subtaskId}
            </Link>
          </h2>
          <h2>
            Lint Verdict:{' '}
            <span
              style={{
                border: '1px solid black',
                borderRadius: '40px',
                color: '#fff',
                backgroundColor: verdictRepresentation.color,
                padding: '5px 10px',
                marginLeft: '10px',
              }}
            >
              {verdictRepresentation.stringRepr}
            </span>
          </h2>
          <p>
            <Bold text={`${verdictRepresentation.stringRepr}: `} />
            <span style={{ fontStyle: 'italic' }}>{verdictRepresentation.description}</span>
          </p>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '10px' }}>
              <Bold text={'Attempt id:'} />{' '}
              <Link href={`/lookup/${attemptId}`} target="_blank">
                {' '}
                {attemptId}
              </Link>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <Bold text={'Attempted at:'} /> {parseDate(attemptedAt)}
            </div>
            <div>
              <Bold text={'Attempted by:'} />{' '}
              <Link href={`/lookup/${attemptedBy}`} target="_blank">
                {attemptedBy}
              </Link>
            </div>
          </div>
        </div>
        <div>
          <div>
            <h3>Lint Results</h3>
            {showLintResults()}
          </div>
        </div>
      </div>
    </div>
  );
};

// sorts the results in verdicts desc order
const sortResultsByLevel = (results: ResponseLinterResult[]): ResponseLinterResult[] => {
  const levelsOrder = [
    LintLevel.Escalate,
    LintLevel.SendBackToAttempt,
    LintLevel.ExcludeFromConsensus,
    LintLevel.Redo,
    LintLevel.Info,
  ];
  return results.sort((a, b) => {
    const indexA = levelsOrder.includes(a.level)
      ? _.indexOf(levelsOrder, a.level)
      : levelsOrder.length;
    const indexB = levelsOrder.includes(b.level)
      ? _.indexOf(levelsOrder, b.level)
      : levelsOrder.length;
    return indexA - indexB;
  });
};

type PassedLint = Pick<ResponseLinterRule, 'name' | 'level'>;

const LintResultsCards = ({ subtaskLintResults }: { subtaskLintResults: SubtaskLintResult }) => {
  const { lintResults, lintRules, linterMetrics } = subtaskLintResults;
  const flattenedResults = _.flatMap(lintResults).filter(
    result => result?.lintOutcome !== LintOutcome.Ignore,
  ) as ResponseLinterResult[];

  const passedLints = flattenedResults.filter(result => result.lintOutcome !== LintOutcome.Fail);
  let failedLints = flattenedResults.filter(result => result.lintOutcome === LintOutcome.Fail);

  if (linterMetrics) {
    failedLints = failedLints.map(result => ({
      ...result,
      metric: linterMetrics.find(metric => metric.lintRuleName === result.name),
    }));
  }

  // sometimes the passed lint-rules are not included in the results
  const fixedPassedLints = _.filter(
    lintRules.map(rule =>
      !_.some(failedLints, failedLint => failedLint.name === rule.name)
        ? { level: rule.level, name: rule.name }
        : null,
    ),
  ) as PassedLint[];
  fixedPassedLints.push(...passedLints.map(lint => ({ level: lint.level, name: lint.name })));

  const orderedFailedLints = sortResultsByLevel(failedLints);
  return (
    <div style={{ marginLeft: '20px' }}>
      {orderedFailedLints.map((lint, ix) => {
        return <FailedLintCard lintResult={lint} lintRules={lintRules} key={ix} />;
      })}
      <div style={{ marginBottom: '40px' }}></div>
      {fixedPassedLints.map((lint, ix) => {
        return <PassedLintCard lintResult={lint} lintRules={lintRules} key={ix} />;
      })}
    </div>
  );
};

const PassedLintCard = ({
  lintResult,
}: {
  lintResult: PassedLint;
  lintRules: ResponseLinterRule[];
}) => {
  const classes = useStyles();
  const { level, name } = lintResult;
  return (
    <div className={classes.cardContained}>
      <h3>
        <FontAwesomeIcon {...levelIcon[ResultLevel.Pass]} /> {_.capitalize(name)}
      </h3>
      <ul>
        <li>
          Post Submission Action: <Bold text={level} />
        </li>
      </ul>
    </div>
  );
};

const GLOBAL_ISSUE = 'global issue';

const parseIssues = (
  metadata?: ScriptLinterMetadata,
  metric?: LintMetric,
): { display: string; dismissalInfo?: LintErrorWithDismissal }[] => {
  if (!metadata) return [];
  if (metadata.message) return [{ display: metadata.message }];
  if (
    _.isArray(metadata.globalIssues) &&
    _.every(metadata.globalIssues, issue => _.isString(issue))
  ) {
    const globalIssues = {
      display: metadata.globalIssues.join('\n'),
    };
    metadata.issues = metadata.issues
      ? { ...metadata.issues, [GLOBAL_ISSUE]: globalIssues }
      : { [GLOBAL_ISSUE]: globalIssues };
  }

  return _.map(metadata?.issues ?? {}, (issue, uuid) => {
    const shortId = uuid.slice(-4);
    let display = '';
    if (!issue.display) {
      display = '';
    } else if (uuid === GLOBAL_ISSUE) {
      display = issue.display;
    } else if (uuid.length > 6 && uuid.includes('-') && !issue.display.includes(shortId)) {
      display = `${shortId} ${issue.display}`;
    } else {
      display = issue.display;
    }
    return {
      display,
      dismissalInfo: metric?.lintErrorsWithDismissal.find(err => err.id === uuid),
    };
  });
};

const FailedLintCard = ({
  lintResult,
  lintRules,
}: {
  lintResult: ResponseLinterResult;
  lintRules: ResponseLinterRule[];
}) => {
  const { name, level, message, metadata, metric } = lintResult;
  const classes = useStyles();
  const parsedIssues = parseIssues(metadata, metric);
  const rule = (lintRules.find(r => r.name === name) ?? {}) as ResponseLinterRule;
  const scriptName = rule?.condition?.scriptName as ScriptLinterName | undefined;
  return (
    <div className={classes.cardContained}>
      <h3>
        <FontAwesomeIcon {...levelIcon[lintLevelResultLevel[level]]} /> {_.capitalize(name)}
      </h3>
      <ul>
        <li>
          Script Name:{' '}
          {scriptName ? (
            <Link href={`/linter_config_docs#${scriptName}`} target="_blank">
              {scriptName}
            </Link>
          ) : (
            'Could not find script-name'
          )}
        </li>
        <li>
          Rule Message: <Bold text={message || '-'} />
        </li>
        <li>
          Post Submission Action: <Bold text={level} />
        </li>
        {parsedIssues.length > 0 && (
          <li>
            Issues:
            {parsedIssues.map(({ display, dismissalInfo }, ix) => (
              <ul key={`issues-${ix}`} className={classes.issuesCard}>
                <li>
                  <p key={ix} style={{ whiteSpace: 'pre' }}>
                    {display}
                  </p>
                </li>
                {dismissalInfo && (
                  <>
                    <ul>
                      <p style={{ fontStyle: 'italic', marginLeft: '-30px' }}>
                        This issue was dismissed for the following reason:
                      </p>
                      <li>
                        Dismissal type: <Bold text={dismissalInfo.dismissalType} />
                      </li>
                      {dismissalInfo.dismissalType === LintDismissalType.Rejected && (
                        <>
                          <li>
                            Reason:{' '}
                            <Bold text={dismissalInfo.dismissalReason ?? 'No reason found'} />
                          </li>
                          <li>
                            Description:{' '}
                            <Bold
                              text={
                                dismissalInfo.dismissalReasonExplanation ?? 'No description found'
                              }
                            />
                          </li>
                        </>
                      )}
                    </ul>
                  </>
                )}
              </ul>
            ))}
          </li>
        )}

        {metadata?.extra && (!scriptName || EXTRA_CONTENT_ALLOWED_LINTERS.includes(scriptName)) && (
          <>
            <li>
              {scriptName
                ? `${_.capitalize(
                    scriptName
                      .replaceAll('SCRIPT_', '')
                      .replaceAll('Script_', '')
                      .replaceAll('-', ' '),
                  )} extra content:`
                : 'Extra content:'}
              <ReactJsonView
                src={metadata.extra}
                name="extra"
                theme="bright"
                iconStyle="triangle"
                collapsed={100}
                collapseStringsAfterLength={120}
                groupArraysAfterLength={false}
                displayObjectSize={true}
                displayDataTypes={false}
                style={{ borderRadius: '10px', padding: '10px', marginRight: '20px' }}
                enableClipboard={true}
              />
            </li>
          </>
        )}

        {rule?.condition?.config && (
          <div style={{ marginTop: '40px' }}>
            <Bold text={'Linter Config:'} />
            <ReactJsonView
              src={rule.condition.config}
              name="config"
              theme="bright"
              iconStyle="triangle"
              collapsed={100}
              collapseStringsAfterLength={60}
              groupArraysAfterLength={false}
              displayObjectSize={false}
              displayDataTypes={false}
              style={{
                borderRadius: '10px',
                padding: '10px',
                marginRight: '20px',
                marginTop: '20px',
              }}
              enableClipboard={true}
            />
          </div>
        )}
      </ul>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  searchContainer: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    marginBottom: '10px',
  },
  line: { stroke: theme.colors.coolGray20, strokeWidth: '5px' },
  cardContained: {
    border: 'solid 2px #f0f0f0',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px',
  },
  issuesCard: {
    marginBottom: '20px',
    border: 'solid 2px #f0f0f0',
    borderRadius: '10px',
    paddingBottom: '10px',
  },
}));

export default ResponseLintResults;
